import { useEffect, useState } from "react";
import { fetchCareerTree, fetchRoles, fetchEmployee } from "../lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Compass, Target } from "lucide-react";

/**
 * CareerRadar — Overlay skill radar comparing the employee's current levels against 
 * up to 3 candidate future roles (from career-tree top nodes or a pinned target).
 */
export default function CareerRadar({ employeeId, targetRoleId }) {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function run() {
      setLoading(true);
      try {
        const [emp, tree, allRoles] = await Promise.all([
          fetchEmployee(employeeId),
          fetchCareerTree(employeeId),
          fetchRoles(),
        ]);
        if (!mounted) return;
        const rolesById = Object.fromEntries((allRoles || []).map((r) => [r.id, r]));

        // Pick up to 3 candidate roles: pinned target first, then top match nodes (excluding current)
        const candidates = (tree.nodes || [])
          .filter((n) => n.type !== "current")
          .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
          .slice(0, 3);
        // If target is pinned, put it first & unique
        if (targetRoleId) {
          const pinned = (tree.nodes || []).find((n) => n.id === targetRoleId);
          if (pinned) {
            const filtered = candidates.filter((c) => c.id !== targetRoleId);
            candidates.splice(0, candidates.length, pinned, ...filtered.slice(0, 2));
          }
        }
        setRoles(candidates.map((c) => ({ ...c, ...rolesById[c.id], pinned: c.id === targetRoleId })));

        // Build axis data: union of top required skills across all candidates (max 6 axes)
        const skillSet = new Map();
        candidates.forEach((c) => {
          const r = rolesById[c.id];
          (r?.required_skills || []).forEach((s) => {
            const cur = skillSet.get(s.skill_id) || { skill: s.skill_name || s.skill_id, skill_id: s.skill_id, importance: 0 };
            cur.importance = Math.max(cur.importance, s.required_level || 3);
            skillSet.set(s.skill_id, cur);
          });
        });
        const axes = Array.from(skillSet.values()).slice(0, 7);

        const empSkills = Object.fromEntries((emp.current_skills || []).map((s) => [s.skill_id, s.current_level]));

        const rows = axes.map((ax) => {
          const row = { skill: ax.skill.length > 14 ? ax.skill.slice(0, 13) + "…" : ax.skill, _fullSkill: ax.skill };
          row["Şu An"] = empSkills[ax.skill_id] || 0;
          candidates.forEach((c) => {
            const r = rolesById[c.id];
            const req = (r?.required_skills || []).find((s) => s.skill_id === ax.skill_id);
            row[c.title] = req ? (req.required_level || 3) : 0;
          });
          return row;
        });
        setData(rows);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    run();
    return () => { mounted = false; };
  }, [employeeId, targetRoleId]);

  const colors = ["#0ea5e9", "#f59e0b", "#a855f7"];

  return (
    <div className="card-surface p-6" data-testid="career-radar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><Compass className="w-3.5 h-3.5" /> Kariyer Radarım</div>
          <h3 className="font-display text-xl font-semibold">Yetkinliklerin vs Aday Roller</h3>
          <p className="text-xs text-slate-500 mt-1">Şu anki seviyen ile en yakın {roles.length} rolün beklentileri.</p>
        </div>
      </div>
      {loading && <div className="h-64 flex items-center justify-center text-sm text-slate-400">Yükleniyor…</div>}
      {!loading && data.length === 0 && (
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">Radar için yeterli veri yok.</div>
      )}
      {!loading && data.length > 0 && (
        <>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={data} outerRadius={110}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11, fill: "#475569" }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <Radar name="Şu An" dataKey="Şu An" stroke="#10b981" fill="#10b981" fillOpacity={0.28} />
              {roles.map((r, i) => (
                <Radar
                  key={r.id}
                  name={r.title + (r.pinned ? " (Hedef)" : "")}
                  dataKey={r.title}
                  stroke={colors[i % colors.length]}
                  fill={colors[i % colors.length]}
                  fillOpacity={r.pinned ? 0.15 : 0.05}
                  strokeDasharray={r.pinned ? "" : "4 3"}
                  strokeWidth={r.pinned ? 2 : 1.5}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
            {roles.map((r, i) => (
              <span key={r.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${r.pinned ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white"}`}>
                {r.pinned && <Target className="w-3 h-3 text-amber-600" />}
                <span className="w-2 h-2 rounded-full" style={{ background: colors[i % colors.length] }} />
                {r.title} · %{r.match_score || 0}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
